import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Card, CardContent, Grid, Chip, Button,
    CircularProgress, Alert,
} from '@mui/material';
import { Icon } from '@iconify/react';
import arrowForwardFill from '@iconify/icons-eva/arrow-forward-fill';
import syndicationApi from '../../utils/syndicationApi';

const STATUS_COLORS = {
    active: 'success',
    presentation: 'primary',
    qa_phase: 'info',
    commitment_phase: 'warning',
    allocating: 'secondary',
    closed: 'default',
};

const INVESTOR_NEXT_STEPS = {
    active: { label: 'Review deal materials', color: 'info.main' },
    presentation: { label: 'Review presentation materials', color: 'primary.main' },
    qa_phase: { label: 'Submit your questions in the Deal Room', color: 'warning.main' },
    commitment_phase: { label: 'Submit your commitment before deadline', color: 'error.main' },
    allocating: { label: 'Allocation in progress \u2014 awaiting confirmation', color: 'secondary.main' },
    closed: { label: 'Deal closed \u2014 check your portfolio', color: 'success.main' },
};

export default function InvestorDealList() {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchDeals();
    }, []);

    const fetchDeals = async () => {
        try {
            const res = await syndicationApi.get('investor/deals/');
            setDeals(res.data?.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load deals.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>My Deals</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {deals.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="h6" color="text.secondary">No deals yet</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            You'll see deals here once you've been invited.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={3}>
                    {deals.map((deal) => (
                        <Grid item xs={12} sm={6} md={4} key={deal.id}>
                            <Card sx={{
                                cursor: 'pointer', transition: 'box-shadow 0.2s',
                                '&:hover': { boxShadow: 6 },
                            }}
                                onClick={() => navigate(`/investor/deals/${deal.deal_code}`)}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1, mr: 1 }}>
                                            {deal.title}
                                        </Typography>
                                        <Chip
                                            label={deal.status?.replace(/_/g, ' ')}
                                            color={STATUS_COLORS[deal.status] || 'default'}
                                            size="small"
                                        />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        {deal.company_name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary">Co-investor Allocation</Typography>
                                        <Typography variant="caption" fontWeight={600}>
                                            {deal.currency_label} {Number(deal.coinvestor_allocation || 0).toLocaleString()}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary">Ticket Range</Typography>
                                        <Typography variant="caption" fontWeight={600}>
                                            {deal.currency_label} {Number(deal.min_ticket_size || 0).toLocaleString()} – {Number(deal.max_ticket_size || 0).toLocaleString()}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Typography variant="caption" color="text.secondary">Instrument</Typography>
                                        <Typography variant="caption" fontWeight={600}>
                                            {deal.instrument_type || '—'}
                                        </Typography>
                                    </Box>
                                    {INVESTOR_NEXT_STEPS[deal.status] && (
                                        <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, px: 1.5, py: 0.75, mb: 1.5, borderLeft: 3, borderColor: INVESTOR_NEXT_STEPS[deal.status].color }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: INVESTOR_NEXT_STEPS[deal.status].color }}>
                                                {INVESTOR_NEXT_STEPS[deal.status].label}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Button
                                        fullWidth variant="outlined" size="small"
                                        endIcon={<Icon icon={arrowForwardFill} />}
                                    >
                                        View Deal Room
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
}
